import { ShoppingCart } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { rupees } from "../products/types";
import { useCartMutations } from "./queries";

interface AddToCartButtonProps {
  productId: string;
  productName: string;
  /** Credits plans: the amount to buy it for. The cart then holds only this plan. */
  amount?: number;
  disabled?: boolean;
  className?: string;
}

/**
 * Adds one of the product; adding it again raises its quantity in the cart. A credits
 * plan is added for `amount` instead.
 */
export function AddToCartButton({ productId, productName, amount, disabled, className }: AddToCartButtonProps) {
  const { add } = useCartMutations();
  const toast = useToast();
  const pending = add.isPending && add.variables?.productId === productId;

  const onClick = () =>
    add.mutate(
      { productId, amount },
      {
        onSuccess: () =>
          toast.success("Added to cart", amount === undefined ? productName : `${productName} · ${rupees(amount)}`),
        onError: (err) =>
          toast.danger("Could not add to cart", err instanceof ApiV2Error ? err.message : "Try again."),
      },
    );

  return (
    <button
      onClick={onClick}
      disabled={pending || disabled}
      className={cn(
        "flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:opacity-70",
        pending ? "cursor-wait" : "disabled:cursor-not-allowed",
        className,
      )}
    >
      <ShoppingCart className="h-4 w-4" /> {pending ? "Adding…" : "Add to cart"}
    </button>
  );
}
